import { clsx } from '@alexis/helpers/clsx';
import React, { forwardRef } from 'react';

import CircleQuestionIcon from '@icons/circle_question.svg';
import CircleTickIcon from '@icons/circle_tick.svg';
import WarningIcon from '@icons/warning.svg';

import styles from './Input.module.scss';

interface InputProps {
  /**
   * @variation email
   * @variation off
   * @variation on
   * @variation street-address
   * @variation username
   */
  autoComplete?: string;
  className?: string;
  hasError?: boolean;
  id?: string;
  inputMode?: 'decimal' | 'email' | 'none' | 'numeric' | 'search' | 'tel' | 'text' | 'url';
  isRtl: boolean;
  isValidated: boolean;
  maxLength?: number;
  message?: string;
  name?: string;
  onBlur?(): void;
  onChange(value: string): void;
  onHelp?(): void;
  placeholder: string;
  style?: React.CSSProperties;
  type?: string;
  value?: string | string[] | number;
  withInfo?: boolean;
  disabled?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      autoComplete,
      className,
      hasError,
      id,
      inputMode,
      isRtl,
      isValidated,
      maxLength,
      message,
      name,
      onBlur,
      onChange,
      onHelp,
      placeholder,
      style,
      type,
      value,
      withInfo,
      disabled,
    },
    ref,
  ): JSX.Element => {
    const defaultInputId = 'inputId';

    return (
      <div
        className={clsx(
          styles.wrapper,
          className,
          isRtl && styles.rtl,
          isValidated && styles.validated,
          !!hasError && styles.error,
        )}
        style={style}
      >
        <div className={styles.inputContainer}>
          <input
            autoComplete={autoComplete}
            id={id ?? defaultInputId}
            inputMode={inputMode}
            name={name}
            onBlur={onBlur}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            ref={ref}
            spellCheck={false}
            type={type}
            // to prevent the input from being uncontrolled by checking if value is null/undefine
            value={value ?? ''}
            disabled={disabled}
          />
          <label htmlFor={id ?? defaultInputId}>{placeholder}</label>

          {isValidated && !hasError ? (
            <CircleTickIcon data-testid='circle-tick-icon' className={styles.validation} />
          ) : null}

          {isValidated && !!hasError ? (
            <WarningIcon data-testid='warning-icon' className={styles.validation} />
          ) : null}

          {!!withInfo && !isValidated ? (
            <CircleQuestionIcon data-testid='circle-question-icon' onClick={onHelp} />
          ) : null}
        </div>

        <div className={styles.messageContainer}>
          <div className={styles.message}>{message}</div>

          {!!maxLength && maxLength > 0 ? (
            <div>
              {!!value ? value.toString().trim().length : 0}/{maxLength}
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

export default Input;
